import React, { Suspense, useEffect, useRef, useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  SubmenuItemProps,
} from "../../assets/interfaces/interfaces";
import { Divider, Icon, ListItemIcon, Tooltip, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import IosShareIcon from "@mui/icons-material/IosShare";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import SharePreviewModal from "../Share/SharePreviewModal";
import classNames from "classnames";
import useEnhancedNavigate from "../Session/helpers";
import { updateTaskStatus, useDeleteChatMutation, useRenameChatMutation } from "./hooks";
import ChipLabel from "../Shared/Chip";
import DeleteModal from "../Shared/DeleteModal";
import { useShowTooltip } from "../../hooks/useShowTooltip";
import { taskLimitByPlan, TaskStatus } from "../Tasks/constants";
import { PauseRounded, PlayArrowRounded, SettingsRounded } from "@mui/icons-material";
import { fetchTask, setSelectedTask, setShowTaskModal, updateTask } from "../../redux/slices/taskSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { useQueryClient } from "@tanstack/react-query";
import { notifyTaskScheduleError } from "../Tasks/helpers";
const SideMenuItem: React.FC<SubmenuItemProps> = ({
  item,
  clearAllChatData,
  chatRef,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [title, setTitle] = useState(item.title);
  const open = Boolean(anchorEl);
  const navigate = useEnhancedNavigate();
  const handleSubmenuClick = (e) => {
    e.stopPropagation(); // This stops the event from bubbling up
    setAnchorEl(e.currentTarget);
  };
  useEffect(() => {
    setTitle(item.title);
  }, [item.title]);
  const { session_id } = useParams();
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleRename = () => {
    setRename(true);
    handleClose();
  };
  const [rename, setRename] = useState(false);
  const user = useSelector((state: RootState) => state.auth.currentUser);
  const deleteChat = useDeleteChatMutation();

  const renameInputRef = useRef<HTMLInputElement>(null);
  // Set focus to the input when renaming
  useEffect(() => {
    if (rename && renameInputRef.current) {
      renameInputRef?.current.focus();
      renameInputRef?.current.select();
    }
  }, [rename, renameInputRef]);
  const renameChat = useRenameChatMutation();

  // Function to handle Enter key press
  const userId = user?.userId;
  const changeTitleReq = () => {
    // optimistic update cached chat history with new title
    renameChat.mutate({ title, sessionId: item.sessionId });
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      changeTitleReq();
      setRename(false); // Set rename to false when Enter is pressed
    }
    if (e.key === "Escape") {
      setRename(false);
    }
  };

  const handleBlur = () => {
    setRename(false); // Cancel the title change when the input loses focus
    // copy chatgpt if title different dispatch title change
    if (title === item.title) {
      changeTitleReq();
    }
  };

  const handleChatDelete = () => {
    setDeleteModal(false);
    handleClose();
    // remove optimistically deleted chat from all cached queries, no matter the search query
    deleteChat.mutate({ sessionId: item.sessionId, userId });

    // navigate away from dead chat
    clearAllChatData();
  };
  const [deleteModal, setDeleteModal] = useState(false);

  const openDeleteModal = () => {
    handleClose();
    setDeleteModal(true);
  };

  const [shareModal, setShareModal] = useState(false);

  const openShareModal = () => {
    handleClose();
    setShareModal(true);
  };

  const isSelectedChat = item.sessionId === session_id;

  const { ref, isTruncated } = useShowTooltip<HTMLDivElement>();

  const isTaskActive = item.task_schedule?.status === TaskStatus.ENABLED;

  const dispatch = useDispatch<AppDispatch>();
  const plan = useSelector((state: { plan }) => state.plan?.plan);
  const { loading } = useSelector((state: RootState) => state.tasks);

  const queryClient = useQueryClient();
  const handleUpdateTask = async (newStatus?: TaskStatus) => {
    try {
      const taskData = await dispatch(fetchTask(item.task_schedule?.id)).unwrap();
      await dispatch(updateTask({
        task: {
          ...taskData,
          status: newStatus,
        },
      })).unwrap();
      item.task_schedule.status = newStatus;
      // optimistic UI update for task status
      updateTaskStatus(queryClient, item.sessionId, item.task_schedule.id, newStatus);
      handleClose();
    }
    catch (error) {
      item.task_schedule.status
      = newStatus === TaskStatus.ENABLED ? TaskStatus.DISABLED : TaskStatus.ENABLED;
      notifyTaskScheduleError(dispatch, taskLimitByPlan[plan.subscription_type], error.error, plan.subscription_type);
    }
  };

  const handleNavigateUserToTask = async () => {
    const taskData = await dispatch(fetchTask(item.task_schedule?.id)).unwrap();
    handleClose();
    navigate(`/tasks`);
    dispatch(setShowTaskModal(true));
    dispatch(setSelectedTask(taskData));
  };

  return (
    <>
      {shareModal && (
        <Suspense fallback={null}>
          <SharePreviewModal
            session_id={item.sessionId}
            open={shareModal}
            handleClose={() => setShareModal(false)}
          />
        </Suspense>
      )}
      {deleteModal && (
        <Suspense fallback={null}>
          <DeleteModal
            handleDelete={handleChatDelete}
            handleCloseWarning={() => setDeleteModal(false)}
            open={deleteModal}
            mainText="Clear Conversation History"
            subText="Are you sure you would like to delete this chat? The deletion is irreversible."
          />
        </Suspense>
      )}
      <Menu
        classes={{
          paper: "!bg-white dark:!bg-background/paper-elevation-16",
          list: "mt-2 min-w-[170px] max-w-xs rounded-lg !text-mui-black-87 dark:!text-white",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <MenuItem
          classes={{
            root: "!flex  !rounded !text-sm !cursor-pointer !focus:ring-0 !hover:bg-white/5 !radix-disabled:pointer-events-none !radix-disabled:opacity-50 !group",
          }}
          onClick={openShareModal}
        >
          <ListItemIcon>
            <IosShareIcon className="!w-[19px] text-action-active relative top-[-2px]" />
          </ListItemIcon>
          Share
        </MenuItem>
        <MenuItem
          classes={{
            root: "!flex !rounded  !text-sm !cursor-pointer !focus:ring-0  !hover:bg-white/5 !radix-disabled:pointer-events-none !radix-disabled:opacity-50 !group",
          }}
          onClick={handleRename}
        >
          <ListItemIcon>
            <DriveFileRenameOutlineIcon className="!w-[20px] text-action-active relative top-[-1px]" />
          </ListItemIcon>
          Rename
        </MenuItem>
        <MenuItem
          classes={{
            root: "!flex !rounded  !text-sm !cursor-pointer !focus:ring-0  !hover:bg-white/5 !radix-disabled:pointer-events-none !radix-disabled:opacity-50 !group",
          }}
          onClick={openDeleteModal}
        >
          <ListItemIcon>
            <DeleteIcon className="!w-[20px] text-action-active relative top-[-1px]" />
          </ListItemIcon>
          Delete Chat
        </MenuItem>

        {/* task related */}
        {item.task_schedule && (
          <div className="flex flex-col">
            <Divider className="!border-black dark:!border-mui-white-12" />
            <Typography variant="caption/regular-400" className="!text-text-disabled ps-4 py-2 w-full text-left">
              Task is
              {" "}
              {isTaskActive ? "active" : "paused" }
            </Typography>

            {isTaskActive
              ? (
                  <MenuItem
                    classes={{
                      root: "!flex !rounded !text-sm !cursor-pointer !focus:ring-0 !hover:bg-white/5 !radix-disabled:pointer-events-none !radix-disabled:opacity-50 !group",
                    }}
                    onClick={() => handleUpdateTask(TaskStatus.DISABLED)}
                    disabled={loading}
                  >
                    <ListItemIcon>
                      <PauseRounded className="!w-[20px] text-action-active relative top-[-1px]" />
                    </ListItemIcon>
                    Pause Task
                  </MenuItem>
                )
              : (
                  <MenuItem
                    classes={{
                      root: "!flex !rounded !text-sm !cursor-pointer !focus:ring-0 !hover:bg-white/5 !radix-disabled:pointer-events-none !radix-disabled:opacity-50 !group",
                    }}
                    onClick={() => handleUpdateTask(TaskStatus.ENABLED)}
                    disabled={loading}
                  >
                    <ListItemIcon>
                      <PlayArrowRounded className="!w-[20px] text-action-active relative top-[-1px]" />
                    </ListItemIcon>
                    Resume Task
                  </MenuItem>
                )}
            <MenuItem
              classes={{
                root: "!flex !rounded  !text-sm !cursor-pointer !focus:ring-0  !hover:bg-white/5 !radix-disabled:pointer-events-none !radix-disabled:opacity-50 !group",
              }}
              onClick={handleNavigateUserToTask}
            >
              <ListItemIcon>
                <SettingsRounded className="!w-[20px] text-action-active relative top-[-1px]" />
              </ListItemIcon>
              Manage Task
            </MenuItem>
          </div>
        )}
      </Menu>
      <div
        className={classNames("px-2 py-[6px] flex items-center justify-between rounded-[4px] cursor-pointer",
          {
            "bg-tgpt-secondary-states-selected": isSelectedChat,
            "hover:bg-text-states-hover": !isSelectedChat,
            "bg-token-surface-primary": (!isSelectedChat && open),
          },
        )}
        onClick={() => navigate(`/chat/${item.sessionId}`)}
        ref={chatRef}
      >
        <div className="dark:text-mui-white-70 whitespace-nowrap overflow-hidden">
          <Typography variant="body2/regular-400" className="flex items-center gap-x-2">
            {item.task_schedule
            && (<ChipLabel label="Task" isActive={item.task_schedule.status === "ENABLED" ? true : false} hasUnreadOutput={item.has_unread_messages} />)}
            {
              rename
                ? (
                    <input
                      onBlur={handleBlur}
                      onKeyDown={handleKeyPress}
                      ref={renameInputRef}
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      className="outline-none p-1 !bg-components-input-filled-enabledFill"
                    />
                  )
                : (
                    <Tooltip placement="bottom" title={title} disableHoverListener={!isTruncated}>
                      <div ref={ref} className="w-[180px] truncate">{title}</div>
                    </Tooltip>
                  )
            }
          </Typography>
        </div>

        <Icon onClick={handleSubmenuClick}>
          <MoreHorizIcon className="text-action-active !flex !items-center" />
        </Icon>

      </div>
    </>
  );
};

export default SideMenuItem;
